import { ReactNode } from 'react'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { Flex } from '@cash-design-system/react'

const CIRCLE_RADIUS = 138
const CIRCLE_DIAMETER = CIRCLE_RADIUS * 2
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS

const MIN_EMPTY_PROGRESS_PERCENT = 0.02
const MIN_PROGRESS_PERCENT = 0.006

const ProgressSvg = styled.svg`
  width: 100%;
`

const Circle = styled.circle`
  display: block;
  fill: none;
`

const CircleBackground = styled(Circle)<{ strokeWidth: number }>`
  stroke: ${props => props.theme.colors.formFieldBackground};
  stroke-width: ${props => props.strokeWidth};
`

const Container = styled(Flex)`
  position: relative;
`

type CircleProgressProps = {
  strokeWidth: number
  strokeDashoffset: number
  minStrokeDashoffset: number
}

const CircleProgress = styled(Circle)<CircleProgressProps>`
  /* Rotate the circle so the progress bar starts at the top center, adjusting for
  the round linecap's length that extends past the stroke length. */
  transform: ${({ strokeWidth }) => {
    const radiansToAccountForRoundLinecap = Math.atan2(strokeWidth / 2, CIRCLE_RADIUS)
    const degreesToAccountForRoundLinecap = (radiansToAccountForRoundLinecap * 180) / Math.PI
    return `rotate(${-90 + degreesToAccountForRoundLinecap}deg);`
  }};
  transform-origin: 50% 50%;

  stroke: ${({ theme }) => theme.colors.primary};
  stroke-width: ${props => props.strokeWidth};
  stroke-linecap: round;
  stroke-dasharray: ${CIRCLE_CIRCUMFERENCE};
  stroke-dashoffset: ${({ strokeDashoffset }) => strokeDashoffset};
  animation: ${({ strokeDashoffset, minStrokeDashoffset }) => {
      return keyframes({
        // Start from the minimum progress bar length instead of 0 to avoid animation when progress is 0%.
        from: { strokeDashoffset: minStrokeDashoffset },
        to: {
          strokeDashoffset,
        },
      })
    }}
    1000ms forwards;
`

const InnerContent = styled(Flex)`
  position: absolute;
`

export function ProgressCircle({
  progressPercent = 0,
  strokeWidthAsPercentageOfRadius = 10 / CIRCLE_RADIUS,
  children,
}: {
  progressPercent: number
  strokeWidthAsPercentageOfRadius?: number
  children?: ReactNode
}) {
  const progressCircumference = CIRCLE_CIRCUMFERENCE * progressPercent
  const minEmptyProgress = CIRCLE_CIRCUMFERENCE * MIN_EMPTY_PROGRESS_PERCENT

  const circleStrokeWidth = strokeWidthAsPercentageOfRadius * CIRCLE_RADIUS
  // The stroke of a circle svg element is centered around the circumference, so the actual diameter is the diameter of the circle plus half a stroke width on each side.
  const circleTotalDiameter = CIRCLE_DIAMETER + circleStrokeWidth

  const xCoordinateCircleCenter = CIRCLE_RADIUS + circleStrokeWidth / 2
  const yCoordinateCircleCenter = CIRCLE_RADIUS + circleStrokeWidth / 2

  const emptyProgressSpaceWillLookTooSmall = CIRCLE_CIRCUMFERENCE - progressCircumference < minEmptyProgress

  if (progressPercent < 1 && emptyProgressSpaceWillLookTooSmall) {
    progressPercent = (CIRCLE_CIRCUMFERENCE - minEmptyProgress) / CIRCLE_CIRCUMFERENCE
  }

  const progressSpaceWillLookTooSmall =
    progressPercent !== 0 && progressCircumference < MIN_PROGRESS_PERCENT * CIRCLE_CIRCUMFERENCE
  if (progressSpaceWillLookTooSmall) {
    progressPercent = MIN_PROGRESS_PERCENT
  }

  // The round linecap extends past the start and end of the stroke, so this extra length
  // has to be accounted for when calculating the offset.
  const roundLinecapLength = circleStrokeWidth / 2
  const progressStrokeLength = CIRCLE_CIRCUMFERENCE * progressPercent - roundLinecapLength * 2
  const progressStrokeDashoffset = progressPercent >= 1 ? 0 : CIRCLE_CIRCUMFERENCE - progressStrokeLength
  const minProgressStrokeLength = CIRCLE_CIRCUMFERENCE * MIN_PROGRESS_PERCENT - roundLinecapLength * 2
  const minProgressStrokeDashoffset = MIN_PROGRESS_PERCENT >= 1 ? 0 : CIRCLE_CIRCUMFERENCE - minProgressStrokeLength

  return (
    <Container justifyContent="center" alignItems="center" fullWidth>
      <ProgressSvg viewBox={`0 0 ${circleTotalDiameter} ${circleTotalDiameter}`}>
        <CircleBackground
          strokeWidth={circleStrokeWidth}
          cx={xCoordinateCircleCenter}
          cy={yCoordinateCircleCenter}
          r={CIRCLE_RADIUS}
        />
        {progressPercent > 0 && (
          <CircleProgress
            strokeWidth={circleStrokeWidth}
            cx={xCoordinateCircleCenter}
            cy={yCoordinateCircleCenter}
            r={CIRCLE_RADIUS}
            radius={CIRCLE_RADIUS}
            strokeDashoffset={progressStrokeDashoffset}
            minStrokeDashoffset={minProgressStrokeDashoffset}
          />
        )}
      </ProgressSvg>
      {children && (
        <InnerContent justifyContent="center" alignItems="center">
          {children}
        </InnerContent>
      )}
    </Container>
  )
}
